import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DateTimePicker from '../DateTimePicker';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const NumberInput = styled.input`
  width: 50px;
  padding: 10px 8px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.15)'};
  border-radius: 6px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  font-size: 14px;
  font-family: inherit;
  text-align: center;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const UnitSelect = styled.select`
  padding: 10px 28px 10px 10px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.15)'};
  border-radius: 6px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 6px center;
  background-repeat: no-repeat;
  background-size: 14px;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  option {
    background: ${(props) =>
      props.theme.name === 'dark' ? '#1E293B' : '#ffffff'};
    color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#1F2937')};
  }
`;

const StaticText = styled.span`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  white-space: nowrap;
`;

const TimePickerWrapper = styled.div`
  width: 120px;
`;

/**
 * Parse a relative date string like "2 weeks before event" into { value, unit }
 */
const parseRelativeDate = (str) => {
  if (!str || typeof str !== 'string') {
    return { value: 1, unit: 'week' };
  }

  const match = str.match(/(\d+)\s+(hours?|days?|weeks?)/i);
  if (match) {
    return {
      value: parseInt(match[1], 10),
      unit: match[2].replace(/s$/, '').toLowerCase(), // normalize: "weeks" -> "week"
    };
  }

  return { value: 1, unit: 'week' }; // default
};

/**
 * Format { value, unit } into a string like "2 weeks before event"
 */
const formatRelativeDate = (value, unit) => {
  const numValue = parseInt(value, 10) || 1;
  const plural = numValue === 1 ? unit : `${unit}s`;
  return `${numValue} ${plural} before event`;
};

/**
 * RelativeDatePicker - A compact inline component for selecting relative date offsets
 *
 * Used in ticket templates where actual event dates aren't known yet.
 * Displays as: [number] [unit dropdown] at [time picker]
 * The "before event" context should be provided in the label.
 *
 * @param {string} value - The relative date string (e.g., "2 weeks before event")
 * @param {function} onChange - Called with the formatted string when value changes
 * @param {string} time - The time value in 24-hour format (e.g., "23:59")
 * @param {function} onTimeChange - Called with the time string when time changes
 * @param {boolean} showTime - Whether to show the time picker (default: true)
 */
const RelativeDatePicker = ({
  value,
  onChange,
  time,
  onTimeChange,
  onUnitChange,
  showTime = true,
}) => {
  const [internalValue, setInternalValue] = useState(() =>
    parseRelativeDate(value)
  );

  // Update internal state when value prop changes
  useEffect(() => {
    const parsed = parseRelativeDate(value);
    setInternalValue(parsed);
  }, [value]);

  const handleValueChange = (e) => {
    const newValue = e.target.value;
    // Allow empty input for typing, but clamp to valid range on blur
    const numValue =
      newValue === ''
        ? ''
        : Math.max(1, Math.min(99, parseInt(newValue, 10) || 1));

    setInternalValue((prev) => ({ ...prev, value: numValue }));

    if (numValue !== '' && onChange) {
      onChange(formatRelativeDate(numValue, internalValue.unit));
    }
  };

  const handleValueBlur = () => {
    // Ensure we have a valid value on blur
    const numValue = parseInt(internalValue.value, 10) || 1;
    setInternalValue((prev) => ({ ...prev, value: numValue }));
    if (onChange) {
      onChange(formatRelativeDate(numValue, internalValue.unit));
    }
  };

  const handleUnitChange = (e) => {
    const newUnit = e.target.value;
    setInternalValue((prev) => ({ ...prev, unit: newUnit }));
    if (onChange) {
      const numValue = parseInt(internalValue.value, 10) || 1;
      onChange(formatRelativeDate(numValue, newUnit));
    }
    if (onUnitChange) {
      onUnitChange(newUnit);
    }
  };

  const handleTimeChange = (newTime) => {
    if (onTimeChange) {
      onTimeChange(newTime);
    }
  };

  // Only show time picker for days/weeks, not hours
  const showTimePicker = showTime && internalValue.unit !== 'hour';

  return (
    <Container>
      <NumberInput
        type="number"
        min="1"
        max="99"
        value={internalValue.value}
        onChange={handleValueChange}
        onBlur={handleValueBlur}
        placeholder="1"
      />
      <UnitSelect value={internalValue.unit} onChange={handleUnitChange}>
        <option value="hour">hour(s)</option>
        <option value="day">day(s)</option>
        <option value="week">week(s)</option>
      </UnitSelect>
      {showTimePicker && (
        <>
          <StaticText>at</StaticText>
          <TimePickerWrapper>
            <DateTimePicker
              value={time || '23:59'}
              onChange={handleTimeChange}
              timeOnly
            />
          </TimePickerWrapper>
        </>
      )}
    </Container>
  );
};

RelativeDatePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  time: PropTypes.string,
  onTimeChange: PropTypes.func,
  onUnitChange: PropTypes.func,
  showTime: PropTypes.bool,
};

RelativeDatePicker.defaultProps = {
  value: '1 week before event',
  time: '23:59',
  showTime: true,
};

export default RelativeDatePicker;
